import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Formik, Form, Field } from "formik";
import { ChevronDown, ChevronUp, MessageSquare } from "lucide-react";
import unlock from "../../../assets/icons/square-unlock-01.svg";
import lock from "../../../assets/icons/square-lock-02.svg";
import imgAddIcon from "../../../assets/icons/add-01.svg";
import deleteImg from "../../../assets/icons/delete-02.svg";
import { statusFormStyles } from "./CleanerStyles";

const CarStatusUpdateForm = ({ onUnlock, onLock, onSubmit, loading }) => {
    const [expandedSection, setExpandedSection] = useState(null);
    const [expandedInspectionItem, setExpandedInspectionItem] = useState(null);
    const [responsiveFormStyles, setResponsiveFormStyles] = useState(statusFormStyles(window.innerWidth));
    const [addNotes, setAddNotes] = useState(false);
    const [buttonHover, setButtonHover] = useState(false);

    const toggleSection = (section) => {
        setExpandedSection(expandedSection === section ? null : section);
    };

    const toggleInspectionItem = (item) => {
        setExpandedInspectionItem(expandedInspectionItem === item ? null : item);
    };

    useEffect(() => {
        const handleResize = () => {
            setResponsiveFormStyles(statusFormStyles(window.innerWidth));
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const handleImageUpload = (event, setFieldValue, fieldName, values) => {
        const files = Array.from(event.target.files);
        const readerPromises = files.map(
            (file) =>
                new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(file);
                }),
        );

        Promise.all(readerPromises).then((newImages) => {
            const existingImages = values.inspection[fieldName].images || [];
            // Concatenate existing images with new images
            setFieldValue(`inspection.${fieldName}.images`, [...existingImages, ...newImages]);
        });
    };

    const handleImageDelete = (index, setFieldValue, fieldName, values) => {
        const images = values.inspection[fieldName].images || [];
        const newImages = images.filter((_, i) => i !== index);
        setFieldValue(`inspection.${fieldName}.images`, newImages);
    };

    const renderTasks = (section, values, setFieldValue) =>
        Object.entries(values[section]).map(([task, value]) => {
            if (section === "inspection") {
                // Render inspection items
                return (
                    <div key={task} style={responsiveFormStyles.inspectionItem}>
                        <div
                            style={responsiveFormStyles.inspectionItemHeader}
                            onClick={() => toggleInspectionItem(task)}>
                            <span>{task.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}</span>
                            {expandedInspectionItem === task ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </div>
                        {expandedInspectionItem === task && (
                            <div style={responsiveFormStyles.inspectionItemContent}>
                                <div style={responsiveFormStyles.imageSection}>
                                    <div style={responsiveFormStyles.imagePreviewContainer}>
                                        {value.images &&
                                            value.images.map((image, index) => (
                                                <div key={index} style={responsiveFormStyles.imageContainer}>
                                                    <img
                                                        src={image}
                                                        alt={`${task} ${index}`}
                                                        style={responsiveFormStyles.image}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            handleImageDelete(index, setFieldValue, task, values)
                                                        }
                                                        style={responsiveFormStyles.deleteImageButton}>
                                                        <img src={deleteImg} alt="delete" />
                                                    </button>
                                                </div>
                                            ))}
                                        <div style={responsiveFormStyles.imageUploadContainer}>
                                            <input
                                                type="file"
                                                id={`${task}-image-upload`}
                                                onChange={(event) =>
                                                    handleImageUpload(event, setFieldValue, task, values)
                                                }
                                                accept="image/*"
                                                multiple
                                                style={{ display: "none" }}
                                            />
                                            <label
                                                htmlFor={`${task}-image-upload`}
                                                style={responsiveFormStyles.addImageButton}>
                                                <img src={imgAddIcon} alt="add image icon" />
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                {!addNotes ? (
                                    <button
                                        type="button"
                                        style={responsiveFormStyles.addNotesButton}
                                        onClick={() => {
                                            setAddNotes(true);
                                        }}>
                                        <MessageSquare size={12} />
                                        Add Notes
                                    </button>
                                ) : (
                                    <Field
                                        as="textarea"
                                        name={`inspection.${task}.notes`}
                                        placeholder="Notes"
                                        style={responsiveFormStyles.textarea}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                );
            }
            return (
                <div key={task} style={responsiveFormStyles.checkboxContainer}>
                    <label htmlFor={`${section}-${task}`} style={responsiveFormStyles.checkboxLabel}>
                        {task.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
                    </label>
                    <Field
                        type="checkbox"
                        name={`${section}.${task}`}
                        id={`${section}-${task}`}
                        style={responsiveFormStyles.checkbox}
                    />
                </div>
            );
        });

    return (
        <div style={responsiveFormStyles.container}>
            <div style={responsiveFormStyles.topButtons}>
                <button
                    onClick={onUnlock}
                    style={{ ...responsiveFormStyles.actionButton, ...responsiveFormStyles.unlockButton }}>
                    <img src={unlock} alt="unlock" />
                    Unlock
                </button>
                <button
                    onClick={onLock}
                    style={{ ...responsiveFormStyles.actionButton, ...responsiveFormStyles.lockButton }}>
                    <img src={lock} alt="lock" />
                    Lock
                </button>
            </div>

            <Formik
                initialValues={{
                    interior: {
                        removeTrash: false,
                        vacuumSeats: false,
                        cleanDashboard: false,
                        cleanCupHolders: false,
                        wipeWindows: false,
                        checkSeats: false,
                        cleanAirVents: false,
                    },
                    exterior: {
                        washExterior: false,
                        checkWheels: false,
                        wipeMirrors: false,
                        removeBirdDroppings: false,
                        cleanLights: false,
                    },
                    inspection: {
                        checkDamages: { images: [], notes: "" },
                        verifyTireCondition: { images: [], notes: "" },
                        checkWipers: { images: [], notes: "" },
                        checkMissingAccessories: { images: [], notes: "" },
                        checkFuelLevel: { images: [], notes: "" },
                        checkWarningLights: { images: [], notes: "" },
                        checkElectronics: { images: [], notes: "" },
                    },
                    finalTouches: {
                        applyAirFreshener: false,
                        restockSupplies: false,
                        verifyDocuments: false,
                        checkSpareTire: false,
                    },
                }}
                onSubmit={onSubmit}>
                {({ values, setFieldValue }) => (
                    <Form>
                        <div style={responsiveFormStyles.scrollableContent}>
                            <h1 style={responsiveFormStyles.title}>Car Status Update Form</h1>
                            {["interior", "exterior", "inspection", "finalTouches"].map((section) => (
                                <div key={section} style={responsiveFormStyles.sectionContainer}>
                                    <div
                                        style={responsiveFormStyles.sectionHeader}
                                        onClick={() => toggleSection(section)}>
                                        <span style={responsiveFormStyles.sectionTitle}>
                                            {section
                                                .replace(/([A-Z])/g, " $1")
                                                .replace(/^./, (str) => str.toUpperCase())}
                                        </span>
                                        {expandedSection === section ? (
                                            <ChevronUp size={20} />
                                        ) : (
                                            <ChevronDown size={20} />
                                        )}
                                    </div>
                                    {expandedSection === section && (
                                        <div style={responsiveFormStyles.sectionContent}>
                                            {renderTasks(section, values, setFieldValue)}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div style={responsiveFormStyles.bottomButton}>
                            <button
                                type="submit"
                                onMouseEnter={() => setButtonHover(true)}
                                onMouseLeave={() => setButtonHover(false)}
                                style={{
                                    ...responsiveFormStyles.submitButton,
                                    ...(loading ? responsiveFormStyles.submitButtonDisabled : {}),
                                    ...(buttonHover && !loading ? responsiveFormStyles.submitButtonHover : {}),
                                }}>
                                {loading ? "Submitting..." : "Submit"}
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

CarStatusUpdateForm.propTypes = {
    onUnlock: PropTypes.func.isRequired,
    onLock: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
};

export default CarStatusUpdateForm;
